// OpenRouter API Client
// Provides access to multiple AI models through a unified interface

const OPENROUTER_API_URL = 'https://openrouter.ai/api/v1/chat/completions';
const OPENROUTER_API_KEY = process.env.REACT_APP_OPENROUTER_KEY;

/**
 * Call OpenRouter API with a chat completion request
 * @param {Object} params - Request parameters
 * @param {string} params.model - Model identifier (e.g., "meta-llama/llama-3.1-8b-instruct:free")
 * @param {Array} params.messages - Array of message objects {role, content}
 * @param {Object} params.options - Additional options (temperature, max_tokens, etc.)
 * @returns {Promise<Object>} - API response
 */
export async function callOpenRouter({ model, messages, options = {} }) {
  if (!OPENROUTER_API_KEY) {
    throw new Error('OpenRouter API key not configured. Please set REACT_APP_OPENROUTER_KEY in your .env file.');
  }

  const requestBody = {
    model,
    messages,
    ...options,
  };

  console.log('=== OPENROUTER API CALL ===');
  console.log('Model:', model);
  console.log('Messages:', messages.length, 'message(s)');
  console.log('Options:', options);

  try {
    const response = await fetch(OPENROUTER_API_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${OPENROUTER_API_KEY}`,
        'Content-Type': 'application/json',
        'HTTP-Referer': window.location.origin,
        'X-Title': 'Omnigeist AI Readiness Assessment',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('OpenRouter API Error:', response.status, errorData);
      throw new Error(
        errorData.error?.message || `OpenRouter API error: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    
    console.log('=== OPENROUTER API SUCCESS ===');
    console.log('Model used:', data.model);
    console.log('Tokens:', data.usage);
    
    return data;
  } catch (error) {
    console.error('=== OPENROUTER API FAILED ===');
    console.error('Error:', error.message);
    throw error;
  }
}

/**
 * Generate recommendations using AI
 * @param {Object} params - Parameters for recommendation generation
 * @param {string} params.systemPrompt - System prompt defining AI's role
 * @param {string} params.userPrompt - User prompt with assessment data
 * @param {string} params.model - Model to use (optional, defaults to free Llama)
 * @returns {Promise<Object>} - Parsed JSON response from AI
 */
export async function generateRecommendations({ 
  systemPrompt, 
  userPrompt, 
  model = 'meta-llama/llama-3.1-8b-instruct:free' 
}) {
  const messages = [
    { role: 'system', content: systemPrompt },
    { role: 'user', content: userPrompt },
  ];

  const options = {
    temperature: 0.3, // Lower temperature for more consistent JSON output
    max_tokens: 1000,
    response_format: { type: 'json_object' }, // Request JSON mode
  };

  try {
    const response = await callOpenRouter({ model, messages, options });
    
    // Extract the assistant's message
    const content = response.choices[0]?.message?.content;
    
    if (!content) {
      throw new Error('No content in API response');
    }

    // Parse JSON response
    const parsedResponse = JSON.parse(content);
    
    console.log('=== AI RECOMMENDATIONS GENERATED ===');
    console.log('Recommendations:', parsedResponse);
    
    return parsedResponse;
  } catch (error) {
    console.error('Error generating AI recommendations:', error);
    throw error;
  }
}

/**
 * Generate recommendations with automatic fallback
 * Tries primary model, then fallback model, then returns null for static recommendations
 * @param {Object} params - Parameters for recommendation generation
 * @returns {Promise<Object|null>} - Recommendations or null if all attempts fail
 */
export async function generateRecommendationsWithFallback(params) {
  const models = [
    'meta-llama/llama-3.1-8b-instruct:free', // Primary: Free and fast
    'google/gemini-flash-1.5',                // Fallback: Cheap and reliable
  ];

  for (const model of models) {
    try {
      console.log(`Attempting to generate recommendations with ${model}...`);
      const result = await generateRecommendations({ ...params, model });
      return result;
    } catch (error) {
      console.warn(`Failed with ${model}:`, error.message);
      // Continue to next model
    }
  }

  // All models failed
  console.error('All AI models failed. Falling back to static recommendations.');
  return null;
}

